import React, { useState, useEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import { ccInput, ccLabel } from "../care/ui";

// Smart address typeahead powered by OpenStreetMap Nominatim (free, no API key)
export default function AddressInput({ value, onChange, onSelect, label = "Address", testId = "address-input" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&countrycodes=us&q=${encodeURIComponent(value)}`,
          { headers: { Accept: "application/json" } }
        );
        if (res.ok) {
          setSuggestions(await res.json());
          setOpen(true);
        }
      } catch (err) {
        console.error("Address suggestions failed", err);
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [value]);

  const pick = (s) => {
    const result = { address: s.display_name, lat: parseFloat(s.lat), lng: parseFloat(s.lon) };
    onChange(s.display_name);
    onSelect?.(result);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid={`${testId}-wrap`}>
      <label className={ccLabel}>{label}</label>
      <div className="relative">
        <MapPin className="w-4 h-4 text-[#E5A0BC] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="Start typing an address…"
          className={`${ccInput} pl-10`}
          data-testid={testId}
          autoComplete="off"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-[#FCE6E9] rounded-2xl shadow-lg overflow-hidden" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-4 py-2.5 text-xs font-semibold text-[#374151] hover:bg-[#FFF9F2] border-b border-[#FCE6E9]/60 last:border-0 transition-colors"
              data-testid={`${testId}-suggestion-${s.place_id}`}
            >
              {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
